import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";

const SESSION_TIMEOUT_MS = 55 * 60 * 1000; // 55 min (token expires at 60)
const WARNING_BEFORE_MS = 5 * 60 * 1000;   // Show warning 5 min before timeout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export default function SessionTimeout() {
  const { token, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
    setShowWarning(false);
    logout();
  }, [logout]);

  const resetTimers = useCallback(() => {
    if (!token) return;
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemaining(WARNING_BEFORE_MS / 1000);
      countdownRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Set logout timer
    timeoutRef.current = setTimeout(handleLogout, SESSION_TIMEOUT_MS);
  }, [token, handleLogout]);

  const handleExtendSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newToken = response.data.access_token;
      localStorage.setItem("thrively_token", newToken);
      resetTimers();
    } catch {
      handleLogout();
    }
  }, [token, resetTimers, handleLogout]);

  // Track user activity
  useEffect(() => {
    if (!token) return;
    const onActivity = () => {
      // Only reset if warning isn't showing (avoid resetting during warning)
      if (!showWarning) {
        const now = Date.now();
        // Throttle: only reset if 60+ seconds since last reset
        if (now - lastActivityRef.current > 60000) {
          resetTimers();
        }
      }
    };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity));
    resetTimers();
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      clearInterval(countdownRef.current);
    };
  }, [token, resetTimers, showWarning]);

  if (!showWarning || !token) return null;

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50" data-testid="session-timeout-warning">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </div>
        <h2 className="text-xl font-heading font-bold text-gray-900 mb-2">Session Expiring</h2>
        <p className="text-gray-600 mb-1">For your security, your session will expire due to inactivity.</p>
        <p className="text-2xl font-bold text-amber-600 mb-6 font-mono">
          {minutes}:{seconds.toString().padStart(2, '0')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleLogout}
            className="flex-1 px-4 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            data-testid="session-logout-btn"
          >
            Sign Out
          </button>
          <button
            onClick={handleExtendSession}
            className="flex-1 px-4 py-3 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-colors"
            data-testid="session-extend-btn"
          >
            Stay Signed In
          </button>
        </div>
      </div>
    </div>
  );
}
